import React from 'react';
import { useFieldArray, type FieldErrors, type FieldError, type Control } from 'react-hook-form';

import {
    ControlledDynamicTextEditor,
    ControlledDynamicEquationEditor,
    type MatrixRow,
    type MatrixColumn,
    type MatrixCell,
    type QtiMatrixQuestionDTO,
} from '@/src/lib';
import { ControlledCheckbox, ControlledSingleSelect, ErrorMessage } from '@/src/ui';
import { type TextEntryModalPartialProps } from '../../_shared';

const cellTypeOptions = [
    { id: 'radio', name: 'Radio' },
    { id: 'check', name: 'Check' },
    { id: 'openText', name: 'Open Text' },
    { id: 'staticText', name: 'Static Text' },
];

interface TableEditorProps {
    question: QtiMatrixQuestionDTO;
    onCellsChange: (cells: MatrixCell[]) => void;
    setModalProps: (props: TextEntryModalPartialProps) => void;
    control: Control<QtiMatrixQuestionDTO, any, QtiMatrixQuestionDTO>;
    errors: FieldErrors<QtiMatrixQuestionDTO>;
}

const TableEditor: React.FC<TableEditorProps> = ({
    question,
    onCellsChange,
    setModalProps,
    control,
    errors,
}) => {
    const { additionalMeta, rows, columns, cells, meta } = question;
    const excludeHeaderRow = additionalMeta.excludeHeaderRow;
    const excludeHeaderColumn = additionalMeta.excludeHeaderColumn;

    const { append: appendRow, remove: removeRow } = useFieldArray({
        control,
        name: 'rows',
    });
    const { append: appendColumn, remove: removeColumn } = useFieldArray({
        control,
        name: 'columns',
    });

    const handleAddRow = () => {
        let nextRowIndex = 1;
        rows.forEach((row) => {
            const rowIndex = parseInt(row.id.replace('row-', ''), 10);
            if (rowIndex >= nextRowIndex) {
                nextRowIndex = rowIndex + 1;
            }
        });
        const rowIndex = nextRowIndex;

        const newRow: MatrixRow = {
            id: `row-${rowIndex}`,
            text: '',
            text_es: '',
        };
        appendRow(newRow);
        ensureAllCellsExist([...rows, newRow], columns);
    };

    const handleRemoveRow = (id: string) => {
        const index = rows.findIndex((row) => row.id === id);
        if (index !== -1) {
            removeRow(index);
            ensureAllCellsExist([...rows.splice(index, 1)], columns);
        }
    };

    const handleAddColumn = () => {
        let nextColumnIndex = 1;
        columns.forEach((col) => {
            const colIndex = parseInt(col.id.replace('col-', ''), 10);
            if (colIndex >= nextColumnIndex) {
                nextColumnIndex = colIndex + 1;
            }
        });
        const columnIndex = nextColumnIndex;

        const newColumn: MatrixColumn = {
            id: `col-${columnIndex}`,
            text: '',
            text_es: '',
        };
        appendColumn(newColumn);
        ensureAllCellsExist(rows, [...columns, newColumn]);
    };

    const handleRemoveColumn = (id: string) => {
        const index = columns.findIndex((col) => col.id === id);
        if (index !== -1) {
            removeColumn(index);
            ensureAllCellsExist(rows, [...columns.splice(index, 1)]);
        }
    };

    const updateCell = (rowId: string, columnId: string, updated: Partial<MatrixCell>) => {
        const index = cells.findIndex((cell) => cell.rowId === rowId && cell.columnId === columnId);
        const existing = index >= 0 ? cells[index] : undefined;

        const baseType = updated.type || existing?.type;

        let updatedCells = [...cells];

        // If the update involves selecting a radio cell as correct
        const wasRadioAndIsNowCorrect = baseType === 'radio' && updated.isCorrect === true;
        const isNowRadioAndWasCorect = updated.type === 'radio' && existing?.isCorrect === true;
        if (wasRadioAndIsNowCorrect || isNowRadioAndWasCorect) {
            // Uncheck all other radio cells in the same row
            updatedCells = updatedCells.map((cell) =>
                cell.rowId === rowId && cell.type === 'radio' ? { ...cell, isCorrect: false } : cell
            );
        }

        const newCell: MatrixCell = {
            rowId,
            columnId,
            type: baseType ?? 'radio',
            isCorrect: updated.isCorrect ?? existing?.isCorrect ?? false,
            staticContent: '',
            staticContent_es: '',
            expectedText: '',
            expectedText_es: '',
        };

        if (index >= 0) {
            updatedCells[index] = newCell;
        } else {
            updatedCells.push(newCell);
        }

        onCellsChange(updatedCells);
    };

    const getCellError = (rowId: string, columnId: string): Record<string, FieldError> | null => {
        const index = cells.findIndex((cell) => cell.rowId === rowId && cell.columnId === columnId);
        if (index === -1) return null;

        const cellErrors = Array.isArray(errors?.cells) ? errors.cells[index] : null;
        return cellErrors || null;
    };

    const ensureAllCellsExist = (updatedRows: MatrixRow[], updatedCols: MatrixColumn[]) => {
        const updatedCells: MatrixCell[] = [];

        for (const row of updatedRows) {
            for (const col of updatedCols) {
                const existing = cells.find((c) => c.rowId === row.id && c.columnId === col.id);
                if (existing) {
                    updatedCells.push(existing);
                } else {
                    updatedCells.push({
                        rowId: row.id,
                        columnId: col.id,
                        type: 'radio',
                        isCorrect: false,
                    });
                }
            }
        }

        onCellsChange(updatedCells);
    };

    return (
        <div className="space-y-6">
            <h3 className="font-semibold">Matrix Editor</h3>
            <div className="flex space-x-4">
                <ControlledCheckbox
                    name="additionalMeta.excludeHeaderRow"
                    label="Exclude Header Row from Student Response"
                    control={control}
                />
                <ControlledCheckbox
                    name="additionalMeta.excludeHeaderColumn"
                    label="Exclude Header Column from Student Response"
                    control={control}
                />
            </div>
            <div className="overflow-x-auto">
                <table className="table-auto border border-gray-400 w-full min-w-max">
                    <thead>
                        <tr>
                            <th className="border p-2 min-w-[200px]"></th>
                            {columns.map((col, colIndex: number) => {
                                const colError = Array.isArray(errors.columns)
                                    ? errors.columns[colIndex]?.text?.message
                                    : undefined;
                                const colError_es = Array.isArray(errors.columns)
                                    ? errors.columns[colIndex]?.text_es?.message
                                    : undefined;
                                return (
                                    <th key={col.id} className="border p-2 min-w-[200px]">
                                        <div className="flex flex-col space-y-1">
                                            <div className="flex items-center space-x-1">
                                                {excludeHeaderRow ? (
                                                    'Header excluded'
                                                ) : (
                                                    <>
                                                        {meta.isSpanishAllowed && (
                                                            <div
                                                                onClick={() =>
                                                                    setModalProps({
                                                                        initialData:
                                                                            columns[colIndex]
                                                                                .text_es,
                                                                        fieldName: `columns.${colIndex}.text_es`,
                                                                        friendlyName: `Col ${colIndex} (Spanish)`,
                                                                        textMode: 'rich',
                                                                        error: colError_es,
                                                                    })
                                                                }
                                                                style={{ cursor: 'pointer' }}
                                                            >
                                                                Edit Spanish
                                                            </div>
                                                        )}
                                                        <ErrorMessage error={colError_es} />
                                                        <ControlledDynamicTextEditor
                                                            name={`columns.${colIndex}.text`}
                                                            control={control}
                                                            initialValue={col.text}
                                                            simpleMode
                                                            style={{ minWidth: '120px' }}
                                                            error={colError}
                                                        />
                                                    </>
                                                )}

                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveColumn(col.id)}
                                                    className="text-red-600 font-bold"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        </div>
                                    </th>
                                );
                            })}
                            <th className="border p-2 min-w-[200px]">
                                <button
                                    type="button"
                                    onClick={handleAddColumn}
                                    className="text-green-600 font-bold"
                                >
                                    + Column
                                </button>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, rowIndex) => {
                            const rowError = Array.isArray(errors.rows)
                                ? errors.rows[rowIndex]?.text?.message
                                : undefined;
                            return (
                                <tr key={row.id}>
                                    <td className="border p-2 min-w-[200px]">
                                        {excludeHeaderColumn ? (
                                            'Header excluded'
                                        ) : (
                                            <div className="flex flex-col space-y-1">
                                                {meta.isSpanishAllowed && (
                                                    <div
                                                        onClick={() =>
                                                            setModalProps({
                                                                initialData: rows[rowIndex].text_es,
                                                                fieldName: `rows.${rowIndex}.text_es`,
                                                                friendlyName: `Row ${rowIndex} (Spanish)`,
                                                                textMode: 'rich',
                                                                error: rowError,
                                                            })
                                                        }
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        Edit Spanish
                                                    </div>
                                                )}
                                                <ErrorMessage error={rowIndex} />
                                                <div className="flex items-center space-x-1">
                                                    <ControlledDynamicTextEditor
                                                        name={`rows.${rowIndex}.text`}
                                                        control={control}
                                                        initialValue={row.text}
                                                        simpleMode
                                                        style={{ minWidth: '120px' }}
                                                        error={rowError}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveRow(row.id)}
                                            className="text-red-600 font-bold"
                                        >
                                            ×
                                        </button>
                                    </td>
                                    {columns.map((col) => {
                                        const cellIndex = cells.findIndex(
                                            (cell) =>
                                                cell.rowId === row.id && cell.columnId === col.id
                                        );
                                        const cell = cells[cellIndex];
                                        const cellError = getCellError(row.id, col.id);
                                        return (
                                            <td
                                                key={col.id}
                                                className="border p-2 min-w-[200px] space-y-1"
                                            >
                                                <ControlledSingleSelect
                                                    name={`cells.${cellIndex}.type`}
                                                    label="Cell type"
                                                    control={control}
                                                    options={cellTypeOptions}
                                                    onChange={(e) =>
                                                        updateCell(row.id, col.id, {
                                                            type: e.target
                                                                .value as MatrixCell['type'],
                                                        })
                                                    }
                                                    error={cellError?.type}
                                                />
                                                {cell?.type === 'staticText' ? (
                                                    <>
                                                        {meta.isSpanishAllowed && (
                                                            <div
                                                                onClick={() =>
                                                                    setModalProps({
                                                                        initialData:
                                                                            cells[cellIndex]
                                                                                .staticContent_es ||
                                                                            '',
                                                                        fieldName: `cells.${cellIndex}.staticContent_es`,
                                                                        friendlyName: `Cell ${cellIndex} (Spanish)`,
                                                                        textMode: 'rich',
                                                                        error: cellError
                                                                            ?.staticContent_es
                                                                            ?.message,
                                                                    })
                                                                }
                                                                style={{ cursor: 'pointer' }}
                                                            >
                                                                Edit Spanish
                                                            </div>
                                                        )}
                                                        <ErrorMessage
                                                            error={cellError?.staticContent_es}
                                                        />
                                                        <ControlledDynamicTextEditor
                                                            key={`cell-${cellIndex}-staticContent`}
                                                            name={`cells.${cellIndex}.staticContent`}
                                                            control={control}
                                                            initialValue={cell?.staticContent || ''}
                                                            simpleMode
                                                            style={{ minWidth: '120px' }}
                                                            error={cellError?.staticContent}
                                                        />
                                                    </>
                                                ) : cell?.type === 'openText' ? (
                                                    <>
                                                        {meta.isSpanishAllowed && (
                                                            <div
                                                                onClick={() =>
                                                                    setModalProps({
                                                                        initialData:
                                                                            cells[cellIndex]
                                                                                .expectedText_es ||
                                                                            '',
                                                                        fieldName: `cells.${cellIndex}.expectedText_es`,
                                                                        friendlyName: `Cell ${cellIndex} (Spanish)`,
                                                                        textMode: 'rich',
                                                                        error: cellError
                                                                            ?.expectedText_es
                                                                            ?.message,
                                                                    })
                                                                }
                                                                style={{ cursor: 'pointer' }}
                                                            >
                                                                Edit Spanish
                                                            </div>
                                                        )}
                                                        <ErrorMessage
                                                            error={cellError?.expectedText_es}
                                                        />
                                                        <ControlledDynamicEquationEditor
                                                            key={`cell-${cellIndex}-openText`}
                                                            contentKey={`cell-${cellIndex}-openText`}
                                                            name={`cells.${cellIndex}.expectedText`}
                                                            control={control}
                                                            initialValue={cell?.expectedText || ''}
                                                            style={{ minWidth: '200px' }}
                                                            showKeypad={
                                                                question.meta.shouldShowKeypad
                                                            }
                                                            allowedKeys={
                                                                question.meta.keypadSetting
                                                            }
                                                            error={cellError?.expectedText}
                                                        />
                                                    </>
                                                ) : (
                                                    <label className="flex items-center space-x-1">
                                                        <ControlledCheckbox
                                                            name={`cells.${cellIndex}.isCorrect`}
                                                            label="Correct"
                                                            control={control}
                                                            checked={cell?.isCorrect || false}
                                                            onChange={(e) => {
                                                                updateCell(row.id, col.id, {
                                                                    isCorrect: e.target.checked,
                                                                });
                                                            }}
                                                        />
                                                    </label>
                                                )}
                                            </td>
                                        );
                                    })}
                                    <td className="border p-2 min-w-[200px]"></td>
                                </tr>
                            );
                        })}
                        <tr>
                            <td>
                                <button
                                    type="button"
                                    onClick={handleAddRow}
                                    className="ml-2 text-blue-600 font-bold"
                                >
                                    + Row
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default TableEditor;
